import { useState, useEffect, useCallback, useMemo } from "react";
import { toast } from "sonner";
import { useFormatters } from "../components/shared/useFormatters";
import {
  Search,
  Users,
  Building2,
  MapPin,
  ClipboardList,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Loader2,
  ChevronDown,
  ChevronRight,
  Merge,
  History,
  FileWarning,
  RefreshCw,
  Save,
  Trash2,
  Undo2,
  Table2,
  AlertCircle,
  Eye,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Input } from "../components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Badge } from "../components/ui/badge";
import { cn } from "../lib/utils";
import { LoadingSpinner } from "../components/shared/DataComponents";
import api, { extractErrorMessage } from "../lib/api";
import { EditableTable } from "../components/cleanup/EditableTable";
import { DependencyPreviewPanel } from "../components/cleanup/DependencyPreviewPanel";

// Entity type configurations for bulk edit (non-operational data only)
const BULK_EDIT_ENTITY_TYPES = [
  { 
    value: "clients", 
    label: "Clients", 
    icon: Building2,
    columns: [
      { key: "name", label: "Name", editable: true, width: "180px" },
      { key: "contact_email", label: "Email", editable: true, width: "180px" },
      { key: "contact_phone", label: "Phone", editable: true, width: "120px" },
      { key: "billing_address", label: "Address", editable: true, width: "250px" },
      { key: "reference_code", label: "Ref", editable: true, width: "80px" },
    ],
    getDisplayName: (r) => r.name || "Unnamed Client",
    sortKey: "name",
  },
  { 
    value: "staff", 
    label: "Crew", 
    icon: Users,
    columns: [
      { key: "first_name", label: "First Name", editable: true, width: "100px" },
      { key: "last_name", label: "Last Name", editable: true, width: "100px" },
      { key: "email", label: "Email", editable: true, width: "180px" },
      { key: "phone", label: "Phone", editable: true, width: "110px" },
      { key: "status", label: "Status", editable: true, width: "80px" },
      { key: "skills", label: "Qualifications", editable: false, type: "array", width: "150px" },
    ],
    getDisplayName: (r) => `${r.first_name || ""} ${r.last_name || ""}`.trim() || "Unnamed",
    sortKey: "last_name",
  },
  { 
    value: "locations", 
    label: "Locations", 
    icon: MapPin,
    columns: [
      { key: "name", label: "Name", editable: true, width: "160px" },
      { key: "address", label: "Address", editable: true, width: "180px" },
      { key: "city", label: "City", editable: true, width: "100px" },
      { key: "postal_code", label: "Postal", editable: true, width: "80px" },
      { key: "country", label: "Country", editable: true, width: "100px" },
    ],
    getDisplayName: (r) => r.name || "Unnamed Location",
    sortKey: "name",
  },
  { 
    value: "task_definitions", 
    label: "Task Definitions", 
    icon: ClipboardList,
    columns: [
      { key: "name", label: "Name", editable: true, width: "180px" },
      { key: "task_type", label: "Type", editable: true, width: "80px" },
      { key: "default_pay_rate", label: "Pay Rate", editable: true, width: "90px", type: "currency" },
      { key: "default_charge_rate", label: "Charge Rate", editable: true, width: "100px", type: "currency" },
      { key: "default_hours", label: "Hours", editable: true, width: "70px", type: "number" },
    ],
    getDisplayName: (r) => r.name || "Unnamed Task",
    sortKey: "name",
  },
];

// Entity types for duplicate detection (includes projects)
const DUPLICATE_ENTITY_TYPES = [
  { value: "staff", label: "Crew", icon: Users },
  { value: "clients", label: "Clients", icon: Building2 },
  { value: "locations", label: "Locations", icon: MapPin },
  { value: "task_definitions", label: "Task Definitions", icon: ClipboardList },
];


// ============== NAME NORMALIZATION PANEL (PROMPT 67) ==============
function NameNormalizationPanel() {
  const [selectedTable, setSelectedTable] = useState("staff");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [executing, setExecuting] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const loadPreview = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/cleanup/names/preview/${selectedTable}`);
      setPreview(res.data);
      setSelectedIds(new Set(res.data.previews?.map(p => p.id) || []));
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to load preview"));
    } finally {
      setLoading(false);
    }
  };

  const executeNormalization = async (applyToAll = false) => {
    if (!preview || preview.changed_count === 0) return;
    
    setExecuting(true);
    try {
      const recordIds = applyToAll ? null : Array.from(selectedIds);
      const res = await api.post(`/cleanup/names/execute/${selectedTable}`, { record_ids: recordIds });
      toast.success(`Normalized ${res.data.updated_count} records`);
      // Refresh preview
      loadPreview();
    } catch (err) {
      toast.error(extractErrorMessage(err, "Failed to execute normalization"));
    } finally {
      setExecuting(false);
    }
  };

  const toggleSelect = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  const selectAll = () => {
    if (preview?.previews) {
      setSelectedIds(new Set(preview.previews.map(p => p.id)));
    }
  };

  const deselectAll = () => {
    setSelectedIds(new Set());
  };

  return (
    <div className="bg-card/50 border border-white/10 rounded-lg p-6 space-y-6" data-testid="name-normalization-panel">
      {/* Header */}
      <div className="flex items-center gap-2 pb-2 border-b border-white/10">
        <Users className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold">Name Normalization</h2>
      </div>
      
      <p className="text-sm text-muted-foreground">
        Normalize names to the platform standard: <strong>First_name LAST_NAME</strong>
        <br />
        First names are capitalized (e.g., John), last names are fully uppercase (e.g., SMITH).
      </p>

      {/* Table Selection */}
      <div className="flex items-center gap-4">
        <Label>Select Table:</Label>
        <div className="flex gap-2">
          <Button
            variant={selectedTable === "staff" ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedTable("staff")}
          >
            <Users className="h-4 w-4 mr-2" />
            Crew
          </Button>
          <Button
            variant={selectedTable === "client_contacts" ? "default" : "outline"}
            size="sm"
            onClick={() => setSelectedTable("client_contacts")}
          >
            <Building2 className="h-4 w-4 mr-2" />
            Client Contacts
          </Button>
        </div>
        <Button onClick={loadPreview} disabled={loading} data-testid="preview-names-btn">
          {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Eye className="h-4 w-4 mr-2" />}
          Preview Changes
        </Button>
      </div>

      {/* Preview Results */}
      {preview && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="text-sm">
              <span className="text-muted-foreground">Total records:</span> {preview.total_records}
              <span className="mx-2">•</span>
              <span className="text-muted-foreground">Need normalization:</span>{" "}
              <span className={preview.changed_count > 0 ? "text-amber-400 font-medium" : "text-green-400"}>
                {preview.changed_count}
              </span>
            </div>
            {preview.changed_count > 0 && (
              <div className="flex gap-2">
                <Button variant="ghost" size="sm" onClick={selectAll}>Select All</Button>
                <Button variant="ghost" size="sm" onClick={deselectAll}>Deselect All</Button>
              </div>
            )}
          </div>

          {preview.changed_count === 0 ? (
            <div className="text-center py-8 text-green-400">
              <CheckCircle2 className="h-8 w-8 mx-auto mb-2" />
              <p className="font-medium">All names are already normalized!</p>
            </div>
          ) : (
            <>
              {/* Preview Table */}
              <div className="border border-white/10 rounded-lg overflow-hidden max-h-[400px] overflow-y-auto">
                <table className="w-full text-sm">
                  <thead className="bg-surface-elevated sticky top-0">
                    <tr>
                      <th className="w-8 p-2"></th>
                      <th className="text-left p-2 font-medium">Current Name</th>
                      <th className="text-left p-2 font-medium">→</th>
                      <th className="text-left p-2 font-medium">Normalized Name</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.previews.map((item) => (
                      <tr 
                        key={item.id} 
                        className={cn(
                          "border-t border-white/5 hover:bg-surface-elevated/50",
                          selectedIds.has(item.id) && "bg-primary/10"
                        )}
                      >
                        <td className="p-2 text-center">
                          <input
                            type="checkbox"
                            checked={selectedIds.has(item.id)}
                            onChange={() => toggleSelect(item.id)}
                            className="rounded"
                          />
                        </td>
                        <td className="p-2">
                          <span className="text-red-400/80">{item.current_first} {item.current_last}</span>
                        </td>
                        <td className="p-2 text-muted-foreground">→</td>
                        <td className="p-2">
                          <span className="text-green-400">{item.normalized_first} {item.normalized_last}</span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-end gap-3 pt-4 border-t border-white/10">
                <Button
                  variant="outline"
                  onClick={() => executeNormalization(false)}
                  disabled={executing || selectedIds.size === 0}
                  data-testid="apply-selected-btn"
                >
                  {executing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Apply to Selected ({selectedIds.size})
                </Button>
                <Button
                  onClick={() => executeNormalization(true)}
                  disabled={executing}
                  data-testid="apply-all-btn"
                >
                  {executing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                  Apply to All ({preview.changed_count})
                </Button>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}


export default function DataCleanupPage() {
  const { formatDate, formatTime } = useFormatters();
  const [activeTab, setActiveTab] = useState("bulk-edit");
  
  // ============== BULK EDIT STATE ==============
  const [bulkEditEntityType, setBulkEditEntityType] = useState("clients");
  const [bulkEditData, setBulkEditData] = useState([]);
  const [bulkEditDependencies, setBulkEditDependencies] = useState({});
  const [bulkEditChanges, setBulkEditChanges] = useState({}); // recordId -> { field -> value }
  const [bulkEditSelected, setBulkEditSelected] = useState(new Set());
  const [bulkEditLoading, setBulkEditLoading] = useState(false);
  const [bulkEditSaving, setBulkEditSaving] = useState(false);
  const [bulkEditSearch, setBulkEditSearch] = useState("");
  const [deletePreviewOpen, setDeletePreviewOpen] = useState(false);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);

  // ============== DUPLICATES STATE ==============
  const [selectedEntityType, setSelectedEntityType] = useState("staff");
  const [duplicateGroups, setDuplicateGroups] = useState([]);
  const [duplicatesLoading, setDuplicatesLoading] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState({});
  
  // Merge preview state
  const [mergePreviewOpen, setMergePreviewOpen] = useState(false);
  const [mergePreview, setMergePreview] = useState(null);
  const [selectedPrimary, setSelectedPrimary] = useState(null);
  const [fieldSelections, setFieldSelections] = useState({});
  const [mergeExecuting, setMergeExecuting] = useState(false);
  
  // ============== AUDIT LOG STATE ==============
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  // Get current entity config for bulk edit
  const currentEntityConfig = useMemo(() => 
    BULK_EDIT_ENTITY_TYPES.find(e => e.value === bulkEditEntityType),
    [bulkEditEntityType]
  );

  // Filter data based on search
  const filteredBulkEditData = useMemo(() => {
    if (!bulkEditSearch.trim()) return bulkEditData;
    const searchLower = bulkEditSearch.toLowerCase();
    return bulkEditData.filter(record => {
      const displayName = currentEntityConfig?.getDisplayName(record) || "";
      const email = record.email || "";
      return displayName.toLowerCase().includes(searchLower) || 
             email.toLowerCase().includes(searchLower);
    });
  }, [bulkEditData, bulkEditSearch, currentEntityConfig]);

  // Count of pending changes
  const pendingChangesCount = useMemo(() => 
    Object.keys(bulkEditChanges).length,
    [bulkEditChanges]
  );

  // Record names map for dependency preview
  const recordNames = useMemo(() => {
    const names = {};
    bulkEditData.forEach(record => {
      names[record.id] = currentEntityConfig?.getDisplayName(record) || record.id;
    });
    return names;
  }, [bulkEditData, currentEntityConfig]);

  // ============== BULK EDIT FUNCTIONS ==============
  
  // Load data for bulk edit
  const loadBulkEditData = useCallback(async () => {
    setBulkEditLoading(true);
    setBulkEditChanges({});
    setBulkEditSelected(new Set());
    
    try {
      // Load entity data
      const entityEndpoints = {
        clients: "/clients",
        staff: "/crew-management",
        locations: "/locations",
        task_definitions: "/task-definitions",
      };
      
      const [dataResponse, depsResponse] = await Promise.all([
        api.get(entityEndpoints[bulkEditEntityType], { params: { limit: 1000 } }),
        api.get(`/cleanup/dependencies/${bulkEditEntityType}`),
      ]);
      
      // Handle paginated response (staff) vs array response
      const rawData = dataResponse.data?.items || dataResponse.data;
      
      // Filter out inactive records
      const activeData = rawData.filter(r => r.is_active !== false);
      
      // Sort alphabetically based on entity type's sortKey
      const entityConfig = BULK_EDIT_ENTITY_TYPES.find(e => e.value === bulkEditEntityType);
      const sortKey = entityConfig?.sortKey || "name";
      const sortedData = [...activeData].sort((a, b) => {
        const aVal = (a[sortKey] || "").toString().toLowerCase();
        const bVal = (b[sortKey] || "").toString().toLowerCase();
        return aVal.localeCompare(bVal);
      });
      
      setBulkEditData(sortedData);
      setBulkEditDependencies(depsResponse.data);
    } catch (err) {
      toast.error(`Failed to load data: ${extractErrorMessage(err)}`);
    } finally {
      setBulkEditLoading(false);
    }
  }, [bulkEditEntityType]);

  // Handle cell change
  const handleCellChange = useCallback((recordId, field, value) => {
    setBulkEditChanges(prev => {
      const recordChanges = prev[recordId] || {};
      const originalRecord = bulkEditData.find(r => r.id === recordId);
      const originalValue = originalRecord?.[field];
      
      // If value is back to original, remove the change
      if (value === originalValue || (value === "" && !originalValue)) {
        const { [field]: _, ...restFields } = recordChanges;
        if (Object.keys(restFields).length === 0) {
          const { [recordId]: __, ...rest } = prev;
          return rest;
        }
        return { ...prev, [recordId]: restFields };
      }
      
      return {
        ...prev,
        [recordId]: { ...recordChanges, [field]: value }
      };
    });
  }, [bulkEditData]);

  // Save all changes
  const saveBulkChanges = async () => {
    if (pendingChangesCount === 0) return;
    
    setBulkEditSaving(true);
    try {
      const updates = Object.entries(bulkEditChanges).map(([id, fields]) => ({
        id,
        ...fields
      }));
      
      const response = await api.patch(`/cleanup/bulk/${bulkEditEntityType}`, { updates });
      
      const { updated, failed } = response.data;
      
      if (updated > 0) {
        toast.success(`Successfully updated ${updated} record(s)`);
      }
      if (failed && failed.length > 0) {
        toast.error(`Failed to update ${failed.length} record(s)`);
      }
      
      // Reload data and clear changes
      await loadBulkEditData();
      setSaveConfirmOpen(false);
    } catch (err) {
      toast.error(`Save failed: ${extractErrorMessage(err)}`);
    } finally {
      setBulkEditSaving(false);
    }
  };

  // Discard all changes
  const discardBulkChanges = useCallback(() => {
    setBulkEditChanges({});
    toast.info("Changes discarded");
  }, []);

  // Delete selected records
  const deleteSelectedRecords = async () => {
    if (bulkEditSelected.size === 0) return;
    
    setBulkEditSaving(true);
    try {
      const ids = Array.from(bulkEditSelected);
      const response = await api.delete(`/cleanup/bulk/${bulkEditEntityType}`, {
        data: { ids, force: false }
      });
      
      const { deleted, blocked } = response.data;
      
      if (deleted > 0) {
        toast.success(`Successfully deleted ${deleted} record(s)`);
      }
      if (blocked && blocked.length > 0) {
        toast.warning(`${blocked.length} record(s) could not be deleted due to dependencies`);
      }
      
      // Reload data
      await loadBulkEditData();
      setDeletePreviewOpen(false);
    } catch (err) {
      toast.error(`Delete failed: ${extractErrorMessage(err)}`);
    } finally {
      setBulkEditSaving(false);
    }
  };

  // Load data when entity type changes
  useEffect(() => {
    if (activeTab === "bulk-edit") {
      loadBulkEditData();
    }
  }, [activeTab, bulkEditEntityType, loadBulkEditData]);

  // ============== DUPLICATES FUNCTIONS ==============
  
  const detectDuplicates = async () => {
    setDuplicatesLoading(true);
    try {
      const response = await api.get(`/cleanup/duplicates/${selectedEntityType}`);
      
      // Sort duplicate groups alphabetically by primary record name
      const sortedGroups = [...response.data].sort((a, b) => {
        const aName = (a.primary_record?.name || a.primary_record?.first_name || "").toString().toLowerCase();
        const bName = (b.primary_record?.name || b.primary_record?.first_name || "").toString().toLowerCase();
        return aName.localeCompare(bName);
      });
      
      setDuplicateGroups(sortedGroups);
      toast.success(`Found ${response.data.length} potential duplicate group(s)`);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setDuplicatesLoading(false);
    }
  };

  const toggleGroup = (groupId) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupId]: !prev[groupId]
    }));
  };

  const openMergePreview = async (group) => {
    try {
      const response = await api.post(`/cleanup/merge/preview/${group.entity_type}`, {
        primary_id: group.primary_record.id,
        merge_ids: group.duplicates.map(d => d.record_id)
      });
      
      setMergePreview(response.data);
      setSelectedPrimary(group.primary_record.id);
      
      const selections = {};
      response.data.field_conflicts.forEach(conflict => {
        selections[conflict.field] = group.primary_record.id;
      });
      setFieldSelections(selections);
      
      setMergePreviewOpen(true);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const executeMerge = async () => {
    if (!mergePreview || !selectedPrimary) return;
    
    setMergeExecuting(true);
    try {
      const response = await api.post(`/cleanup/merge/execute/${mergePreview.entity_type}`, {
        primary_id: selectedPrimary,
        merge_ids: mergePreview.records_to_merge.map(r => r.id),
        field_selections: fieldSelections
      });
      
      toast.success(response.data.message);
      setMergePreviewOpen(false);
      setMergePreview(null);
      
      detectDuplicates();
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setMergeExecuting(false);
    }
  };

  // ============== AUDIT LOG FUNCTIONS ==============
  
  const loadAuditLogs = async () => {
    setAuditLoading(true);
    try {
      const response = await api.get("/cleanup/audit-log", {
        params: { limit: 50 }
      });
      setAuditLogs(response.data.items);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    } finally {
      setAuditLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "audit") {
      loadAuditLogs();
    }
  }, [activeTab]);

  // ============== HELPER FUNCTIONS ==============

  const getConfidenceBadge = (confidence) => {
    const styles = {
      high: "bg-red-500/10 text-red-500 border-red-500/20",
      medium: "bg-yellow-500/10 text-yellow-500 border-yellow-500/20",
      low: "bg-blue-500/10 text-blue-500 border-blue-500/20",
    };
    return (
      <span className={cn(
        "px-2 py-0.5 text-xs font-medium rounded border uppercase",
        styles[confidence] || ""
      )}>
        {confidence}
      </span>
    );
  };

  const getActionTypeBadge = (actionType) => {
    const styles = {
      duplicate_detection: "bg-blue-500/10 text-blue-500",
      merge_preview: "bg-yellow-500/10 text-yellow-500",
      merge_executed: "bg-green-500/10 text-green-500",
      warning_resolved: "bg-purple-500/10 text-purple-500",
      warning_accepted: "bg-surface-elevated/30 text-muted-foreground",
    };
    return (
      <span className={cn(
        "px-2 py-0.5 text-xs font-medium rounded",
        styles[actionType] || "bg-surface-elevated/30"
      )}>
        {actionType.replace(/_/g, " ")}
      </span>
    );
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return "N/A";
    return `${formatDate(dateStr)} ${formatTime(dateStr)}`;
  };

  const getRecordDisplay = (record, entityType) => {
    if (entityType === "staff") {
      return `${record.first_name} ${record.last_name} (${record.email})`;
    } else if (entityType === "clients") {
      return `${record.name}`;
    } else if (entityType === "locations") {
      return `${record.name} - ${record.address}`;
    } else if (entityType === "projects") {
      return `${record.name}`;
    } else if (entityType === "task_definitions") {
      return `${record.name}`;
    }
    return record.id;
  };

  return (
    <div className="space-y-6" data-testid="data-cleanup-page">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-bold tracking-tight">Data Cleanup</h1>
        <p className="text-sm text-muted-foreground">
          Bulk edit, identify duplicates, merge records, and manage non-operational data
        </p>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-5 lg:w-auto lg:inline-grid">
          <TabsTrigger value="bulk-edit" className="gap-2" data-testid="tab-bulk-edit">
            <Table2 className="h-4 w-4" />
            Bulk Edit
          </TabsTrigger>
          <TabsTrigger value="names" className="gap-2" data-testid="tab-names">
            <Users className="h-4 w-4" />
            Names
          </TabsTrigger>
          <TabsTrigger value="duplicates" className="gap-2" data-testid="tab-duplicates">
            <Search className="h-4 w-4" />
            Duplicates
          </TabsTrigger>
          <TabsTrigger value="warnings" className="gap-2" data-testid="tab-warnings">
            <FileWarning className="h-4 w-4" />
            Warnings
          </TabsTrigger>
          <TabsTrigger value="audit" className="gap-2" data-testid="tab-audit">
            <History className="h-4 w-4" />
            Audit Log
          </TabsTrigger>
        </TabsList>

        {/* ============== BULK EDIT TAB ============== */}
        <TabsContent value="bulk-edit" className="space-y-4">
          {/* Entity Type Selection + Actions */}
          <div className="bg-card/50 border border-white/10 rounded-lg p-4 space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              {/* Entity Type Buttons */}
              <div className="flex flex-wrap gap-2">
                {BULK_EDIT_ENTITY_TYPES.map((type) => {
                  const Icon = type.icon;
                  return (
                    <button
                      key={type.value}
                      onClick={() => setBulkEditEntityType(type.value)}
                      className={cn(
                        "flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors",
                        bulkEditEntityType === type.value
                          ? "bg-primary/10 border-primary text-primary"
                          : "bg-white/5 border-white/10 hover:bg-white/10"
                      )}
                      data-testid={`bulk-edit-type-${type.value}`}
                    >
                      <Icon className="h-4 w-4" />
                      {type.label}
                    </button>
                  );
                })}
              </div>

              {/* Search */}
              <div className="flex-1 max-w-xs">
                <Input
                  placeholder="Search records..."
                  value={bulkEditSearch}
                  onChange={(e) => setBulkEditSearch(e.target.value)}
                  className="h-9"
                  data-testid="bulk-edit-search"
                />
              </div>

              {/* Refresh Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={loadBulkEditData}
                disabled={bulkEditLoading}
              >
                <RefreshCw className={cn("h-4 w-4 mr-2", bulkEditLoading && "animate-spin")} />
                Refresh
              </Button>
            </div>

            {/* Action Bar */}
            <div className="flex items-center justify-between pt-2 border-t border-white/10">
              <div className="flex items-center gap-3">
                {/* Selection Info */}
                {bulkEditSelected.size > 0 && (
                  <Badge variant="secondary">
                    {bulkEditSelected.size} selected
                  </Badge>
                )}
                {pendingChangesCount > 0 && (
                  <Badge className="bg-blue-500/20 text-blue-400">
                    {pendingChangesCount} pending change{pendingChangesCount !== 1 ? "s" : ""}
                  </Badge>
                )}
              </div>

              <div className="flex items-center gap-2">
                {/* Discard Changes */}
                {pendingChangesCount > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={discardBulkChanges}
                    data-testid="discard-changes-btn"
                  >
                    <Undo2 className="h-4 w-4 mr-2" />
                    Discard
                  </Button>
                )}

                {/* Save Changes */}
                <Button
                  size="sm"
                  onClick={() => setSaveConfirmOpen(true)}
                  disabled={pendingChangesCount === 0 || bulkEditSaving}
                  data-testid="save-changes-btn"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Changes
                </Button>

                {/* Delete Selected */}
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => setDeletePreviewOpen(true)}
                  disabled={bulkEditSelected.size === 0 || bulkEditSaving}
                  data-testid="delete-selected-btn"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Selected
                </Button>
              </div>
            </div>
          </div>

          {/* Editable Table */}
          {bulkEditLoading ? (
            <div className="flex items-center justify-center py-16">
              <LoadingSpinner />
            </div>
          ) : (
            <EditableTable
              data={filteredBulkEditData}
              columns={currentEntityConfig?.columns || []}
              dependencies={bulkEditDependencies}
              selectedIds={bulkEditSelected}
              onSelectionChange={setBulkEditSelected}
              onCellChange={handleCellChange}
              changes={bulkEditChanges}
              entityType={currentEntityConfig?.label || "record"}
            />
          )}

          {/* Record Count */}
          <div className="text-sm text-muted-foreground text-right">
            Showing {filteredBulkEditData.length} of {bulkEditData.length} records
          </div>
        </TabsContent>

        {/* ============== NAME NORMALIZATION TAB (PROMPT 67) ============== */}
        <TabsContent value="names" className="space-y-4">
          <NameNormalizationPanel />
        </TabsContent>

        {/* ============== DUPLICATES TAB ============== */}
        <TabsContent value="duplicates" className="space-y-4">
          {/* Entity Type Selection */}
          <div className="bg-card/50 border border-white/10 rounded-lg p-6 space-y-4">
            <div className="flex items-center gap-2 pb-2 border-b border-white/10">
              <Search className="h-5 w-5 text-primary" />
              <h2 className="text-lg font-semibold">Duplicate Detection</h2>
            </div>

            <div className="flex flex-wrap gap-4">
              {DUPLICATE_ENTITY_TYPES.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.value}
                    onClick={() => setSelectedEntityType(type.value)}
                    className={cn(
                      "flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors",
                      selectedEntityType === type.value
                        ? "bg-primary/10 border-primary text-primary"
                        : "bg-white/5 border-white/10 hover:bg-white/10"
                    )}
                    data-testid={`entity-type-${type.value}`}
                  >
                    <Icon className="h-4 w-4" />
                    {type.label}
                  </button>
                );
              })}
            </div>

            <div className="flex justify-end">
              <Button
                onClick={detectDuplicates}
                disabled={duplicatesLoading}
                data-testid="detect-duplicates-btn"
              >
                {duplicatesLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Scanning...
                  </>
                ) : (
                  <>
                    <Search className="h-4 w-4 mr-2" />
                    Scan for Duplicates
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Duplicate Groups */}
          {duplicateGroups.length > 0 ? (
            <div className="space-y-4">
              {duplicateGroups.map((group) => (
                <div
                  key={group.group_id}
                  className="bg-card/50 border border-white/10 rounded-lg overflow-hidden"
                  data-testid={`duplicate-group-${group.group_id}`}
                >
                  {/* Group Header */}
                  <div
                    className="p-4 flex items-center justify-between cursor-pointer hover:bg-white/5"
                    onClick={() => toggleGroup(group.group_id)}
                  >
                    <div className="flex items-center gap-3">
                      {expandedGroups[group.group_id] ? (
                        <ChevronDown className="h-5 w-5" />
                      ) : (
                        <ChevronRight className="h-5 w-5" />
                      )}
                      <div>
                        <div className="font-medium">
                          {getRecordDisplay(group.primary_record, group.entity_type)}
                        </div>
                        <div className="text-sm text-muted-foreground">
                          {group.duplicates.length + 1} potential duplicate(s)
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {getConfidenceBadge(group.highest_confidence)}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          openMergePreview(group);
                        }}
                        data-testid={`merge-preview-btn-${group.group_id}`}
                      >
                        <Merge className="h-4 w-4 mr-1" />
                        Merge Preview
                      </Button>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedGroups[group.group_id] && (
                    <div className="border-t border-white/10 p-4 bg-white/5">
                      <div className="space-y-3">
                        {/* Primary Record */}
                        <div className="flex items-center gap-2 p-3 bg-green-500/10 rounded-lg">
                          <CheckCircle2 className="h-4 w-4 text-green-500" />
                          <span className="text-sm font-medium text-green-500">Primary:</span>
                          <span className="text-sm">
                            {getRecordDisplay(group.primary_record, group.entity_type)}
                          </span>
                        </div>

                        {/* Duplicate Records */}
                        {group.duplicates.map((dup) => (
                          <div
                            key={dup.record_id}
                            className="flex items-center justify-between p-3 bg-white/5 rounded-lg"
                          >
                            <div className="flex items-center gap-2">
                              <AlertTriangle className="h-4 w-4 text-yellow-500" />
                              <span className="text-sm">
                                {getRecordDisplay(dup.record_data, group.entity_type)}
                              </span>
                            </div>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">
                                {dup.match_reason}
                              </span>
                              {getConfidenceBadge(dup.confidence)}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : duplicatesLoading ? null : (
            <div className="bg-card/50 border border-white/10 rounded-lg p-12 text-center">
              <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">No Duplicates Found</h3>
              <p className="text-sm text-muted-foreground">
                Click &quot;Scan for Duplicates&quot; to check for potential duplicate records
              </p>
            </div>
          )}
        </TabsContent>

        {/* ============== WARNINGS TAB ============== */}
        <TabsContent value="warnings" className="space-y-4">
          <div className="bg-card/50 border border-white/10 rounded-lg p-12 text-center">
            <FileWarning className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">Import Warnings</h3>
            <p className="text-sm text-muted-foreground">
              Warnings from data imports will appear here for review and resolution.
              <br />
              Use the Data Import page to import data first.
            </p>
          </div>
        </TabsContent>

        {/* ============== AUDIT LOG TAB ============== */}
        <TabsContent value="audit" className="space-y-4">
          <div className="bg-card/50 border border-white/10 rounded-lg overflow-hidden">
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="font-semibold">Cleanup Audit Log</h3>
              <Button
                variant="outline"
                size="sm"
                onClick={loadAuditLogs}
                disabled={auditLoading}
              >
                <RefreshCw className={cn("h-4 w-4 mr-2", auditLoading && "animate-spin")} />
                Refresh
              </Button>
            </div>

            {auditLoading ? (
              <div className="p-8 flex justify-center">
                <LoadingSpinner />
              </div>
            ) : auditLogs.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-white/5 border-b border-white/10">
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">
                        Timestamp
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">
                        Action
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">
                        Entity Type
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-muted-foreground uppercase">
                        Details
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-white/5">
                    {auditLogs.map((log) => (
                      <tr key={log.id} className="hover:bg-white/5">
                        <td className="px-4 py-3 text-sm">
                          {formatDateTime(log.performed_at)}
                        </td>
                        <td className="px-4 py-3">
                          {getActionTypeBadge(log.action_type)}
                        </td>
                        <td className="px-4 py-3 text-sm capitalize">
                          {log.entity_type.replace(/_/g, " ")}
                        </td>
                        <td className="px-4 py-3 text-sm text-muted-foreground">
                          {log.action_type === "duplicate_detection" && (
                            <span>{log.details?.groups_found || 0} groups found</span>
                          )}
                          {log.action_type === "merge_executed" && (
                            <span>
                              Merged {log.details?.merged_ids?.length || 0} record(s) into{" "}
                              {log.details?.primary_id?.substring(0, 8)}...
                            </span>
                          )}
                          {log.action_type === "merge_preview" && (
                            <span>{log.record_ids?.length || 0} records previewed</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-8 text-center text-muted-foreground">
                No audit log entries yet
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* ============== DIALOGS ============== */}

      {/* Save Confirmation Dialog */}
      <AlertDialog open={saveConfirmOpen} onOpenChange={setSaveConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Save Changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You are about to save {pendingChangesCount} change{pendingChangesCount !== 1 ? "s" : ""} to {currentEntityConfig?.label || "records"}.
              This action cannot be easily undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={saveBulkChanges}
              disabled={bulkEditSaving}
              data-testid="confirm-save-btn"
            >
              {bulkEditSaving ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                "Save Changes"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Preview Panel */}
      <DependencyPreviewPanel
        open={deletePreviewOpen}
        onOpenChange={setDeletePreviewOpen}
        entityType={bulkEditEntityType}
        recordIds={Array.from(bulkEditSelected)}
        recordNames={recordNames}
        onConfirmDelete={deleteSelectedRecords}
      />

      {/* Merge Preview Dialog */}
      <Dialog open={mergePreviewOpen} onOpenChange={setMergePreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Merge className="h-5 w-5" />
              Merge Preview
            </DialogTitle>
            <DialogDescription>
              Review and confirm the merge operation. Select values for conflicting fields.
            </DialogDescription>
          </DialogHeader>

          {mergePreview && (
            <div className="space-y-6">
              {/* Warnings */}
              {mergePreview.warnings.length > 0 && (
                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-lg p-4">
                  {mergePreview.warnings.map((warning, idx) => (
                    <div key={idx} className="flex items-start gap-2 text-yellow-500">
                      <AlertTriangle className="h-4 w-4 mt-0.5" />
                      <span className="text-sm">{warning}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Field Conflicts */}
              {mergePreview.field_conflicts.length > 0 && (
                <div className="space-y-4">
                  <h3 className="font-semibold">Field Conflicts</h3>
                  <p className="text-sm text-muted-foreground">
                    Select which value to keep for each conflicting field:
                  </p>
                  {mergePreview.field_conflicts.map((conflict) => (
                    <div key={conflict.field} className="bg-white/5 rounded-lg p-4">
                      <Label className="font-medium capitalize mb-2 block">
                        {conflict.field.replace(/_/g, " ")}
                      </Label>
                      <RadioGroup
                        value={fieldSelections[conflict.field] || mergePreview.primary_record.id}
                        onValueChange={(value) =>
                          setFieldSelections((prev) => ({
                            ...prev,
                            [conflict.field]: value,
                          }))
                        }
                      >
                        <div className="flex items-center space-x-2 p-2 bg-green-500/10 rounded">
                          <RadioGroupItem
                            value={mergePreview.primary_record.id}
                            id={`${conflict.field}-primary`}
                          />
                          <Label htmlFor={`${conflict.field}-primary`} className="flex-1 cursor-pointer">
                            <span className="text-green-500 text-xs mr-2">Primary:</span>
                            {String(conflict.primary_value || "(empty)")}
                          </Label>
                        </div>
                        {conflict.other_values.map((other) => (
                          <div
                            key={other.record_id}
                            className="flex items-center space-x-2 p-2 bg-white/5 rounded"
                          >
                            <RadioGroupItem
                              value={other.record_id}
                              id={`${conflict.field}-${other.record_id}`}
                            />
                            <Label
                              htmlFor={`${conflict.field}-${other.record_id}`}
                              className="flex-1 cursor-pointer"
                            >
                              {String(other.value || "(empty)")}
                            </Label>
                          </div>
                        ))}
                      </RadioGroup>
                    </div>
                  ))}
                </div>
              )}

              {/* Dependent Records */}
              {mergePreview.dependent_records.length > 0 && (
                <div className="space-y-2">
                  <h3 className="font-semibold">
                    Dependent Records ({mergePreview.dependent_records.length})
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    These records will be updated to reference the primary record:
                  </p>
                  <div className="max-h-40 overflow-y-auto bg-white/5 rounded-lg p-3 space-y-1">
                    {mergePreview.dependent_records.map((dep, idx) => (
                      <div key={idx} className="text-sm flex items-center gap-2">
                        <CheckCircle2 className="h-3 w-3 text-green-500" />
                        <span className="text-muted-foreground">{dep.collection}:</span>
                        <span>{dep.display_value}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Financial Records (Read-only warning) */}
              {mergePreview.financial_records.length > 0 && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-red-500 mt-0.5" />
                    <div>
                      <p className="font-medium text-red-500">
                        Financial Records (Will NOT be modified)
                      </p>
                      <p className="text-sm text-red-500/80 mt-1">
                        The following financial records reference merged records and will remain unchanged:
                      </p>
                      <ul className="mt-2 text-sm text-red-500/80 list-disc list-inside">
                        {mergePreview.financial_records.map((fin, idx) => (
                          <li key={idx}>{fin.display_value}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setMergePreviewOpen(false)}
            >
              Cancel
            </Button>
            <Button
              onClick={executeMerge}
              disabled={mergeExecuting}
              className="bg-green-600 hover:bg-green-700"
              data-testid="execute-merge-btn"
            >
              {mergeExecuting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Merging...
                </>
              ) : (
                <>
                  <Merge className="h-4 w-4 mr-2" />
                  Confirm Merge
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
